import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Send, CheckCircle2, MapPin, Loader2, ShieldCheck } from 'lucide-react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { api } from '@/lib/api';
import DatePicker from '@/components/ui/date-picker';
import { logger } from '@/lib/logger';
import PG_IMAGES from '@/constants/publicImages';

// hCaptcha \u2014 test site key passes for any submission. Replace via env for prod.
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-000000000001';

const INTERESTS = [
  'Court booking',
  'Coaching & academy',
  'Tournaments & events',
  'Cafe & lounge',
  'Corporate / group',
  'Other',
];

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', interest: INTERESTS[0], preferred_date: '', message: '' });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in your name, email and message.');
      return;
    }
    if (!captchaToken) {
      toast.error('Please complete the anti-spam check.');
      return;
    }
    setSaving(true);
    try {
      const message = form.preferred_date
        ? `${form.message.trim()}\n\nPreferred date: ${form.preferred_date}`
        : form.message.trim();
      await api.post('/public/messages', {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        interest: form.interest,
        message,
        captcha_token: captchaToken,
      });
      setDone(true);
      toast.success('Message sent \u2014 we\u2019ll be in touch soon.');
    } catch (err) {
      logger.error('Contact submit failed', err);
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
      try { captchaRef.current?.resetCaptcha?.(); } catch { /* ignore */ }
      setCaptchaToken('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id="contact" data-testid="public-contact-section" className="relative py-16 sm:py-24 overflow-hidden isolate">
      <div className="absolute inset-0 -z-10">
        <img src={PG_IMAGES.collage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F5F0E6]/98 via-[#F5F0E6]/94 to-[#EFE8DB]/98" />
      </div>
      <div className="max-w-[1360px] mx-auto px-4 sm:px-6 grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] gap-8 lg:gap-14">
        <div className="rounded-2xl border border-[#0F2E23]/10 bg-white shadow-[0_30px_80px_rgba(15,46,35,0.12)] p-6 sm:p-10">
          <div className="text-[10px] sm:text-[11px] tracking-[0.28em] text-[#0F2E23] font-semibold">
            GET&nbsp; IN&nbsp; TOUCH&nbsp;&nbsp;<span className="inline-block w-12 sm:w-16 h-px bg-[#141414]/40 align-middle" />
          </div>
          <h2 className="mt-3 font-display text-[30px] sm:text-[48px] leading-[1.02] tracking-tight text-[#141414]">
            Come play with us.<br/>
            <span style={{ fontFamily: 'Great Vibes, cursive' }} className="italic text-[#0F2E23]">Let’s talk padel.</span>
          </h2>
          <p className="mt-3 max-w-xl text-[14px] text-[#141414]/70">Book a court, join our academy, host a tournament, or drop by for a coffee. Send us a note and we’ll get back within one business day.</p>

          {done ? (
            <div data-testid="contact-success" className="mt-8 text-center py-6">
              <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-[#0F2E23] text-[#F5C86A] mb-4"><CheckCircle2 className="w-7 h-7" /></span>
              <h3 className="font-display text-2xl sm:text-3xl text-[#0F2E23]">Message received</h3>
              <p className="mt-2 text-sm text-[#141414]/70 max-w-md mx-auto">Thanks for reaching out. The Padel Gems team will get back to you at <b>{form.email}</b> within one business day.</p>
              <button onClick={() => { setDone(false); setForm({ name: '', email: '', phone: '', interest: INTERESTS[0], preferred_date: '', message: '' }); setCaptchaToken(''); try { captchaRef.current?.resetCaptcha?.(); } catch { /* */ } }} className="pg-glossy pg-glossy--outline mt-5 text-xs px-4 py-2 rounded-xl border border-[#0F2E23]/20 hover:bg-[#0F2E23]/5">Send another message</button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="mt-8 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name"><input data-testid="contact-name" value={form.name} onChange={(e) => patch('name', e.target.value)} className="pg-cf" required /></Field>
                <Field label="Email"><input data-testid="contact-email" value={form.email} onChange={(e) => patch('email', e.target.value)} type="email" className="pg-cf" required /></Field>
                <Field label="Phone (optional)"><input data-testid="contact-phone" value={form.phone} onChange={(e) => patch('phone', e.target.value)} placeholder="+966" className="pg-cf" /></Field>
                <Field label="I'm interested in">
                  <select data-testid="contact-interest" value={form.interest} onChange={(e) => patch('interest', e.target.value)} className="pg-cf">
                    {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Preferred date (optional)">
                    <DatePicker value={form.preferred_date} onChange={(v) => patch('preferred_date', v)} showTime variant="public" placeholder="When would you like to visit?" minDate={new Date()} testId="contact-date" />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Message">
                    <textarea data-testid="contact-message" value={form.message} onChange={(e) => patch('message', e.target.value)} rows={4} className="pg-cf resize-none" placeholder="Tell us how we can help..." required />
                  </Field>
                </div>
              </div>

              <div data-testid="contact-captcha" className="pt-1">
                <HCaptcha
                  ref={captchaRef}
                  sitekey={HCAPTCHA_SITE_KEY}
                  onVerify={(t) => setCaptchaToken(t)}
                  onExpire={() => setCaptchaToken('')}
                  onError={() => setCaptchaToken('')}
                  theme="light"
                  size="normal"
                />
              </div>

              <div className="pt-2 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[11px] text-[#141414]/50 inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#0F2E23]" /> Protected by hCaptcha</p>
                <button data-testid="contact-submit" disabled={saving} type="submit" className="pg-glossy inline-flex items-center gap-2 bg-[#0F2E23] hover:bg-[#153F30] disabled:opacity-60 text-[#F5F0E6] px-6 py-3 rounded-xl text-[11px] tracking-[0.22em] font-semibold shadow-[0_12px_30px_rgba(15,46,35,0.35)] transition-colors">
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send message</>}
                </button>
              </div>
              <style>{`.pg-cf{width:100%;border:1px solid rgba(20,20,20,.14);background:#fff;color:#141414;border-radius:.75rem;padding:.75rem .9rem;outline:none;transition:border-color .15s, box-shadow .15s;font-size:14px;}.pg-cf::placeholder{color:rgba(20,20,20,.35);}.pg-cf:focus{border-color:#0F2E23;box-shadow:0 0 0 3px rgba(15,46,35,.15);}`}</style>
            </form>
          )}
        </div>

        <aside>
          <div className="relative rounded-2xl overflow-hidden min-h-[240px] lg:min-h-[480px] shadow-[0_30px_60px_rgba(15,46,35,0.25)] h-full">
            <img src={PG_IMAGES.aerialComplex} alt="Padel Gems complex at golden hour" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F2E23]/95 via-[#0F2E23]/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 text-[#F5F0E6]">
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] font-semibold text-[#F5C86A]"><MapPin className="w-3.5 h-3.5" /> FIND US</div>
              <div className="font-display text-3xl sm:text-4xl mt-2 leading-tight">Near Jabal Al Qarah</div>
              <div className="text-[12px] tracking-[0.24em] text-[#F5F0E6]/80 mt-1">AL AHSA  ·  SAUDI ARABIA</div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-[#141414]/60 font-semibold mb-1.5 block">{label}</span>
    {children}
  </label>
);

export default Contact;

